import { am5, am5xy, am5stock } from './amcharts.js';

const colours = {
	blue: am5.color(0x00428E),
	navy: am5.color(0x1e5182),
	green: am5.color(0x008000),
	pink: am5.color(0xD34E84),
	red: am5.color(0xe10004),
	black: am5.color(0x000000),
	white: am5.color(0xffffff),
	purple: am5.color(0x800080),
	yellow: am5.color(0xffd90d),
	lightgrey: am5.color(0xb2b2b2),
	orange: am5.color(0xff8a00),
	grey: am5.color(0xc0c0c0),
};

const extraColours = [
	colours.navy,
	colours.orange,
	colours.yellow,
	colours.green,
	colours.red,
	colours.black,
];

const groupings = {
	daily: [
		{ timeUnit: 'day', count: 1 },
		{ timeUnit: 'week', count: 1 },
		{ timeUnit: 'month', count: 1 },
		{ timeUnit: 'year', count: 1 },
	],
	weekly: [
		{ timeUnit: 'week', count: 1 },
		{ timeUnit: 'month', count: 1 },
		{ timeUnit: 'year', count: 1 },
	],
	monthly: [
		{ timeUnit: 'month', count: 1 },
		{ timeUnit: 'year', count: 1 },
	],
	yearly: [
		{ timeUnit: 'year', count: 1 },
	],
};

const bigNumberPrefixes = [
	{ 'number': 1e+3, 'suffix': 'K' },
	{ 'number': 1e+6, 'suffix': 'M' },
	{ 'number': 1e+9, 'suffix': 'B' },
	{ 'number': 1e+12, 'suffix': 'T' },
];

const values = {
	margins: 32,
	strokeDash: [ 6, 4 ],
	panelHeight: 140,
	scrollbarHeight: 36,
	mainPanelHeight: 440,
	mainPanelHeightSm: 300,
};

const isStandalone = window.config.url.data[0] === 'chart';
const isScreenShot = (isStandalone && window.isWidget === false);

function draw(id, settings, defaultDates = null) {
	if (window.config.bot) {
		return;
	}

	return new Chart(id, settings, defaultDates);
}

class Chart {
	root;
	chart;
	panel;
	xAxis;
	yAxis;
	scrollbar;
	legend;
	cursor;
	cursors = [];

	constructor(id, settings, defaultDates = null) {
		const root = am5.Root.new(id);

		root.utc = true;

		this.root = root;
		this.dataFormatter = settings.dataFormatter;
		this.settings = settings;

		this.defaultDates = defaultDates;
		this.cursor = settings.cursor !== false ? (settings.cursor ?? {}) : null;

		const chart = root.container.children.push(
			am5stock.StockChart.new(root, {
				paddingTop: 0,

				... (settings.chart ?? {}),
			})
		);

		this.chart = chart;

		this.refreshData(settings.data);

		const { panel, yAxis, xAxis } = this.addPanel(settings.panel ?? {}, settings.yAxis ?? {}, settings.xAxis ?? {});
		this.panel = panel;
		this.yAxis = yAxis;
		this.xAxis = xAxis;

		this.scrollbar = this.hasScrollBar() ? this.addScrollbar(this.panel, settings.scrollbar ?? {}) : null;
		this.legend = this.hasLegend() ? this.addLegend(this.panel, settings.legend ?? {}) : null;

		if (!settings.skipChartReady) {
			this.chartReady();
		}
	}

	hasScrollBar() {
		if (isStandalone) {
			return false;
		}

		return this.settings.scrollbar !== false;
	}

	hasLegend() {
		return this.settings.legend !== false;
	}

	zoom(start, end) {
		console.log(`zooming chart to ${start.toISOString()} to ${end.toISOString()}`);

		this.xAxis.zoomToDates(start, end);
	}

	refreshData(data) {
		const start = this.xAxis ? this.xAxis.getPrivate('selectionMin') : null;
		const end = this.xAxis ? this.xAxis.getPrivate('selectionMax') : null;

		this.data = data;

		if (this.dataFormatter) {
			this.data = this.dataFormatter(this.data);
		}

		for (const panel of this.chart.panels) {
			for (const series of panel.series) {
				series.data.setAll(this.data);

				if (start && end) {
					series.events.once('datavalidated', ev => {
						this.zoom(new Date(start), new Date(end));
					});
				}
			}
		}

		if (this.scrollbar) {
			for (const series of this.scrollbar.chart.series) {
				series.data.setAll(this.data);

				if (start && end) {
					series.events.once('datavalidated', ev => {
						for (const axis of this.scrollbar.chart.yAxes) {
							axis.zoom(0, 1);
						}
					});
				}
			}
		}
	}

	setExtraPanels() {
		this.root.dom.style.setProperty(
			'--extra-panels',
			this.chart.panels.length - 1);

		for (const panel of this.chart.panels) {
			panel.set('height', this.calculatePanelHeight(panel.get('originalHeight')));
		}
	}

	calculatePanelHeight(height, newPanel = false) {
		if (window.config.url.data[0] !== 'chart') {
			return height;
		}

		let totalHeight = newPanel ? height : 0;

		for (const panel of this.chart.panels) {
			totalHeight += panel.get('originalHeight');
		}

		return am5.percent(100 * height / totalHeight);
	}

	addPanel(panelSettings = {}, yAxisSettings = {}, xAxisSettings = {}) {
		const baseHeight = this.root.dom.classList.contains('chart-div-sm') ? values.mainPanelHeightSm : values.mainPanelHeight;
		const height = baseHeight - (this.hasScrollBar() ? values.scrollbarHeight : 0);

		const settings = {
			panX: true,
			panY: false,
			name: 'main',
			height,

			... panelSettings,
		};

		settings.originalHeight = settings.height;
		settings.height = this.calculatePanelHeight(settings.height, true);

		const panel = this.chart.panels.push(am5stock.StockPanel.new(this.root, settings));

		panel.panelControls.set('forceHidden', true);

		const yAxis = this.addYAxis(panel, yAxisSettings ?? {});
		const xAxis = this.addXAxis(panel, xAxisSettings ?? {});

		let cursor;

		if (this.cursor) {
			const cursorSettings = {
				... this.cursor,
				name: settings.name,
			};

			cursor = this.addCursor(panel, cursorSettings);

			this.cursors.push(cursor);

			// for (const cursor of this.cursors) {
			// 	cursor.set('syncWith', this.cursors);
			// }
		}

		this.setExtraPanels();

		return { panel, yAxis, xAxis, cursor };
	}

	removePanel(name) {
		const remove = [];

		this.chart.panels.each((panel, index) => {
			if (panel.get('name') === name) {
				remove.push(index);
			}
		});

		for (const index of remove.reverse()) {
			this.chart.panels.removeIndex(index);
		}

		this.setExtraPanels();

		this.#refreshLegend();
	}

	defaultZoom(series) {
		if (this.defaultDates) {
			const defaultDates = [ ... this.defaultDates ];

			this.zoom(defaultDates[0], defaultDates[1]);

			series.events.once('datavalidated', ev => {
				this.zoom(defaultDates[0], defaultDates[1]);
			});

			this.defaultDates = false;
		}
	}

	addSeries(series, panel = this.panel) {
		if (panel === this.panel) {
			this.defaultZoom(series);
		}

		series.data.setAll(this.data);
		panel.series.push(series);

		this.#refreshLegend();

		return series;
	}

	removeSeries(name, panel = this.panel) {
		const remove = [];

		panel.series.each((series, index) => {
			if (series.get('name') === name) {
				remove.push(index);
			}
		});

		for (const index of remove.reverse()) {
			panel.series.removeIndex(index);
		}

		this.#refreshLegend();
	}

	lineSeries(settings = {}, yAxis = this.yAxis, xAxis = this.xAxis) {
		settings.stroke ??= colours.pink;

		const series = this.#series(settings, am5xy.LineSeries, settings.stroke, yAxis, xAxis);

		series.strokes.template.setAll({
			strokeWidth: 2,

			... _.omit(settings, [ 'tooltip', 'bullets', 'fills', 'strokes' ]),
			... (settings.strokes ?? {}),
		});

		if (settings.fills) {
			series.fills.template.setAll(settings.fills);
		}

		if( settings.range ) {
			const rangeDataItem = yAxis.makeDataItem({
				value: settings.range.value,
				endValue: settings.range.endValue,
			});

			const range = series.createAxisRange(rangeDataItem);

			range.strokes.template.setAll({
				stroke: settings.range.color,
			});

			range.fills.template.setAll({
				fill: settings.range.color,
				fillOpacity: 0.15,
				visible: true,
			});

		}

		return series;
	}

	dotSeries(settings = {}, yAxis = this.yAxis, xAxis = this.xAxis) {
		settings.stroke ??= colours.pink;

		const series = this.#series(settings, am5xy.LineSeries, settings.stroke, yAxis, xAxis);

		series.strokes.template.setAll({
			strokeWidth: 2,
			strokeOpacity: 0,

			... _.omit(settings, [ 'tooltip', 'bullets', 'fills', 'strokes' ]),
		});

		series.bullets.push(function(root) {
			return am5.Bullet.new(root, {
				sprite: am5.Circle.new(root, {
					fill: settings.stroke,
					radius: 3,
				})
			});
		});

		return series;
	}

	candlestickSeries(settings = {}, yAxis = this.yAxis, xAxis = this.xAxis) {
		settings.stroke ??= colours.black;

		const series = this.#series(settings, am5xy.CandlestickSeries, settings.stroke, yAxis, xAxis);

		series.columns.template.states.create('riseFromOpen', {
			// fill: colours.black,
			stroke: colours.black,
		});

		series.columns.template.states.create('riseFromPrevious', {
			fill: colours.black,
			stroke: colours.black,
		});

		series.columns.template.states.create('dropFromOpen', {
			// fill: colours.red,
			stroke: colours.red,
		});

		series.columns.template.states.create('dropFromPrevious', {
			fill: colours.red,
			stroke: colours.red,
		});

		series.columns.template.get('themeTags').push('pro');

		series.columns.template.setAll({
			strokeWidth: 1,
			width: am5.percent(100),
		});

		return series;
	}

	columnSeries(settings = {}, yAxis = this.yAxis, xAxis = this.xAxis) {
		settings.stroke ??= colours.pink;

		const series = this.#series(settings, am5xy.ColumnSeries, settings.stroke, yAxis, xAxis);

		series.columns.template.setAll({
			... _.omit(settings, [ 'tooltip', 'bullets', 'fills', 'strokes' ]),
		});

		return series;
	}

	#series(settings, seriesClass, tooltipStroke, yAxis = this.yAxis, xAxis = this.xAxis) {
		const seriesSettings = {
			xAxis,
			yAxis,

			valueXField: 'timestamp',
			visibleInLegend: true,
			legendLabelText: settings.legendLabelText || settings.title,
			legendValueText: '{valueY}',

			... _.omit(settings, [ 'tooltip', 'bullets', 'fills', 'strokes', 'numberFormatter' ]),
		};

		if (seriesSettings.legendLabelText) {
			seriesSettings.legendLabelText = `${seriesSettings.legendLabelText}: `;
		}

		if (settings.numberFormatter) {
			seriesSettings.numberFormatter = am5.NumberFormatter.new(this.root, settings.numberFormatter);
		}

		//for the analyze  section use last value as default legend value.
		if( window.config.url && window.config.url.data[0]=='analyze' ) {
			for( const item of this.settings.data ) {
				if( item['symbol'] && item[settings.name] ) {
					seriesSettings.legendRangeValueText = item[settings.name];
				}
			}
		}

		const series = seriesClass.new(this.root, seriesSettings);

		if (settings.bullets) {
			for (const bullet of settings.bullets) {
				series.bullets.push(() =>
					am5.Bullet.new(this.root, {
						... _.omit(bullet, [ 'sprite', 'type' ]),

						sprite: am5[ bullet.type ?? 'Circle' ].new(this.root, {
							... (bullet.sprite ?? {}),
						}),
					})
				);
			}
		}

		if (settings.tooltip) {
			const tooltip = am5.Tooltip.new(this.root, {
				getFillFromSprite: false,
				getStrokeFromSprite: false,
				autoTextColor: false,

				... settings.tooltip,
			});

			series.set('tooltip', tooltip);

			tooltip.label.setAll({
				fill: '#000000',
				oversizedBehavior: 'wrap',
				maxWidth: 300,
				fontSize: 13,
			});

			tooltip.get('background').setAll({
				fill: '#ffffff',
				fillOpacity: .8,
				stroke: settings.tooltip.stroke ?? tooltipStroke,
			});

			if (settings.tooltip.labelFunction) {
				tooltip.label.adapters.add('text', (text, target) => {
					const data = target?._dataItem?.dataContext;

					if (!data) {
						return;
					}

					return settings.tooltip.labelFunction(data);
				});
			}
		}

		return series;
	}

	addLegend(panel, settings = {}) {
		const id = `${this.root.dom.id}_legend`;
		let legendDiv = document.getElementById(id);
		const newDiv = !legendDiv;

		if (newDiv) {
			legendDiv = document.createElement('div');

			legendDiv.id = id;
			legendDiv.classList.add('chart-legend-div');

			this.root.dom.insertAdjacentElement('afterend', legendDiv);
		}

		const legendRoot = am5.Root.new(id);
		const legendChart = legendRoot.container.children.push(
			am5stock.StockChart.new(legendRoot, {})
		);

		const legend = am5.Legend.new(legendRoot, {
			clickTarget: 'none',
			width: am5.percent(100),
			centerX: am5.percent(50),
			x: am5.percent(50),
			paddingBottom: 8,

			layout: am5.GridLayout.new(legendRoot, {
				fixedWidthGrid: !isStandalone,
			}),

			... _.omit(settings, [ 'labels', 'valueLabels', 'markers', 'markerRectangles' ]),
		});

		legend.events.on('boundschanged', function() {
			legendDiv.style.height = legend.height() + 'px';
		});

		legendChart.children.push(legend);

		const textSettings = {
			fontSize: isStandalone ? 9 : 12,
			lineHeight: isStandalone ? .5 : 1.2,
		};

		const labels = {
			... textSettings,

			fontWeight: 'bold',
			maxWidth: isStandalone ? 65 : 80,
			oversizedBehavior: 'wrap',

			... (settings.labels ?? {})
		};

		const valueLabels = {
			... textSettings,

			paddingRight: 0,
			numberFormatter: am5.NumberFormatter.new(legendRoot, {
				numberFormat: '#.##a',
			}),

			... (settings.valueLabels ?? {})
		};
		
		const markers = {
			width: textSettings.fontSize - 1,
			height: textSettings.fontSize - 1,
			centerY: am5.percent(isStandalone ? 70 : 50),
			
			... (settings.markers ?? {})
		};
		
		const markerRectangles = {
			cornerRadiusTL: 2,
			cornerRadiusTR: 2,
			cornerRadiusBL: 2,
			cornerRadiusBR: 2,
			
			... (settings.markerRectangles ?? {})
		};

		const itemContainers = {
			paddingTop: 0,
			paddingBottom: 0,
			paddingLeft: 0,
			paddingRight: 0,
			marginTop: 0,
			marginBottom: 0,
			marginLeft: 0,
			marginRight: 0,
		};

		legend.labels.template.setAll(labels);
		legend.valueLabels.template.setAll(valueLabels);
		legend.markers.template.setAll(markers);
		legend.markerRectangles.template.setAll(markerRectangles);
		legend.itemContainers.template.setAll(itemContainers);

		return legend;
	}

	#refreshLegend() {
		if (!this.legend) {
			return;
		}

		const seriesToShow = [];

		for (const panel of this.chart.panels) {
			for (const series of panel.series) {
				if (series.get('visibleInLegend')) {
					seriesToShow.push(series);
				}
			}
		}

		this.legend.data.setAll(seriesToShow);
	}

	addScrollbar(panel, settings = {}) {
		const scrollbar = am5xy.XYChartScrollbar.new(this.root, {
			orientation: 'horizontal',
			height: values.scrollbarHeight,

			... settings,
		});

		panel.set('scrollbarX', scrollbar);
		// panel.set('paddingTop', 0);

		const xAxis = this.addXAxis(scrollbar.chart, { syncWithAxis: this.xAxis });
		const yAxis = this.addYAxis(scrollbar.chart);

		this.addSeries(
			this.lineSeries(
				{
					valueXField: 'timestamp',
					name: 'scrollbar',
					stroke: colours.grey,

					... (settings.series ?? {}),
				},
				yAxis,
				xAxis
			),
			scrollbar.chart
		);

		return scrollbar;
	}

	addGuide(value, axis, settings = {}) {
		const rangeDataItem = axis.makeDataItem({
			value,
			endValue: null,
		});

		const range = axis.createAxisRange(rangeDataItem);

		range.get('grid').setAll({
			stroke: colours.blue,
			strokeOpacity: 1,
			location: 1,
			layer: 10,

			... settings,
		});
	}

	addCursor(panel, settings = {}) {
		const cursor = am5xy.XYCursor.new(this.root, {
			... settings,
		});

		panel.set('cursor', cursor);
		// chart.toolsContainer.children.push(cursor);

		return cursor;
	}

	addXAxis(panel, settings = {}) {
		if (!settings.type) {
			settings.type = 'GaplessDateAxis';
		}

		const axisSettings = {
			baseInterval: {
				timeUnit: 'day',
				count: 1,
			},

			... _.omit(settings, [ 'type' ]),
		};

		// swap back to DateAxis?
		const xAxis = this.#axis(
			axisSettings,
			am5xy[ settings.type ],
			am5xy.AxisRendererX,
			'X'
		);

		panel.xAxes.push(xAxis);

		xAxis.data.setAll(this.data);

		return xAxis;
	}

	addYAxisWithPrefixes(panel, settings = {}, prefixes={}) {
		const yAxis = this.addYAxis(panel,settings);
		yAxis.set('numberFormatter', am5.NumberFormatter.new(this.root, prefixes));

		return yAxis;
	}

	addYAxis(panel, settings = {}) {
		const yAxis = this.#axis(settings, am5xy.ValueAxis, am5xy.AxisRendererY, 'Y');

		yAxis.set('numberFormatter', am5.NumberFormatter.new(this.root, {
			numberFormat: '#.##a',
		}));

		panel.yAxes.push(yAxis);

		return yAxis;
	}

	setPrefixesForYAxis(prefixes={}){
		this.yAxis.set('numberFormatter', am5.NumberFormatter.new(this.root, prefixes));
	}

	#axis(settings, axisClass, axisRenderer, side) {
		const tooltip = am5.Tooltip.new(this.root, settings.tooltip ?? {});

		const renderer = axisRenderer.new(this.root, {
			opposite: false,
			minGridDistance: side === 'X' ? 100 : 50,

			... (settings.renderer ?? {}),
		});

		const axis = axisClass.new(this.root, {
			renderer,

			... _.omit(settings, [ 'opposite', 'tooltip', 'grid', 'ticks', 'renderer' ]),

			tooltip,
		});

		if (settings.grid) {
			renderer.grid.template.setAll(settings.grid);
		}

		if (settings.ticks) {
			renderer.ticks.template.setAll(settings.ticks);
		}

		if (settings.grid) {
			renderer.grid.template.setAll(settings.grid);
		}

		axis.get('renderer').labels.template.setAll({
			fontSize: 10,
		});

		tooltip.label.setAll({
			fontSize: 10,
		});

		return axis;
	}

	onReady(callback) {
		let timeout;

		const frameend = () => {
			if (timeout) {
				clearTimeout(timeout);
			}

			timeout = setTimeout(() => {
				this.root.events.off('frameended', frameend);
				callback();
			}, 100);
		};

		this.root.events.on('frameended', frameend);
	}

	chartReady() {
		this.onReady(() => console.log('chart_ready'));
	}

	toggleSeries(name, value = null) {
		for (const panel of this.chart.panels) {
			for (const series of panel.series) {
				if (series.get('name') === name) {
					if (value === null) {
						value = !series.visible;
					}

					if (value) {
						series.show();
					} else {
						series.hide();
					}

					return;
				}
			}
		}
	}

	addEarnings() {
		return this.addSeries(
			this.lineSeries({
				name: 'earnings',
				legendLabelText: 'Earnings',
				visibleInLegend: false,
				valueYField: 'earnings',
				fill: colours.blue,
				opacity: 0,
				tooltip: {
					stroke: colours.blue,
					labelFunction({ earnings_text }) {
						return earnings_text;
					},
				},
				bullets: [
					{
						locationX: 0.5,
						locationY: 0.5,
						sprite: {
							stroke: colours.blue,
							strokeWidth: 2,
							fill: colours.white,
							radius: 10,
							layer: 12,
						},
					},
					{
						type: 'Label',
						locationX: 0.5,
						locationY: 0.5,
						sprite: {
							text: '$',
							textAlign: 'center',
							fill: colours.blue,
							fontSize: 12,
							fontWeight: 'bold',
							populateText: true,
							centerX: am5.percent(50),
							centerY: am5.percent(50),
							layer: 13,
						},
					},
				],
			})
		);
	}
}

const ChartSettings = {
	colours,
	extraColours,
	bigNumberPrefixes,
	draw,
	groupings,
	isScreenShot,
	values,
	am5,
};

export default ChartSettings;
